import { API_CONFIG } from '@/config/api';
import { useAuthStore } from '@/stores/useAuthStore';
import { httpClient } from '@/utils/httpClient';

export interface CatalogImageMetadata {
  hasImage: boolean;
  fileName?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

class CatalogImageService {
  private getBasePath(): string {
    return `${API_CONFIG.ACTIONS_API_BASE_PATH}/catalog`;
  }

  getImageUrl(catalogId: string): string {
    return `${API_CONFIG.BASE_URL}${this.getBasePath()}/${catalogId}/image`;
  }

  async getImageObjectUrl(catalogId: string): Promise<string> {
    if (!catalogId) {
      throw new Error('Catalog ID is required.');
    }

    const imageUrl = this.getImageUrl(catalogId);

    const { accessToken } = useAuthStore.getState();

    const response = await fetch(imageUrl, {
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load catalog image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async uploadImage(catalogId: string, file: File): Promise<void> {
    if (!catalogId) {
      throw new Error('Catalog ID is required.');
    }

    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      throw new Error('Only JPEG and PNG images are supported.');
    }

    if (file.size === 0) {
      throw new Error('Image file must not be empty.');
    }

    if (file.size > MAX_IMAGE_BYTES) {
      throw new Error('Image file exceeds maximum allowed size of 5MB.');
    }

    const base64 = await this.readFileAsBase64(file);

    await httpClient.post<void>(`${this.getBasePath()}/${catalogId}/image`, {
      base64,
      fileName: file.name,
      mimeType: file.type,
    });
  }

  async getMetadata(catalogId: string): Promise<CatalogImageMetadata> {
    if (!catalogId) {
      throw new Error('Catalog ID is required.');
    }

    return httpClient.get<CatalogImageMetadata>(
      `${this.getBasePath()}/${catalogId}/image/metadata`,
    );
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const [, base64] = result.split(',');
          resolve(base64 || '');
          return;
        }
        reject(new Error('Failed to read image file as base64.'));
      };

      reader.onerror = () => {
        reject(new Error('Failed to read image file.'));
      };

      reader.readAsDataURL(file);
    });
  }
}

export const catalogImageService = new CatalogImageService();
